import { useEffect, useRef, useState } from 'react';

import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import { ChevronUpIcon, ChevronDownIcon, CheckIcon } from '../../components/Icons';
import Typography from '../Typography';

import { getInitials } from '../../utils/common';
import { COLORS } from '../../themev2';
import classes from './DropdownListWithActions.module.scss';

type Option = {
  value: any;
  label: string;
};

type Action = {
  icon: JSX.Element;
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

type DropdownListWithActionsProps = {
  options: Option[];
  currentOption: Option;
  setCurrentOption: (option: Option) => void;
  actions: Action[];
  isCollapsed?: boolean;
  openDrawer: () => void;
};

const DropdownListWithActions = ({
  options,
  currentOption,
  setCurrentOption,
  actions,
  isCollapsed,
  openDrawer,
}: DropdownListWithActionsProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickCollapsed = () => {
    openDrawer();
    setIsOpen(true);
  };

  const handleSelectOption = (item: Option) => {
    setCurrentOption(item);
  };

  useEffect(() => {
    // close with external click
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    // close with escape key
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  return (
    <>
      {isCollapsed ? (
        <>
          <Box className={classes.collapsedDropdown} onClick={handleClickCollapsed}>
            <Typography
              sx={{
                color: COLORS.brand.white,
                fontWeight: '400',
                textAlign: 'center',
                fontSize: { xs: '16px', md: '14px' },
                lineHeight: { xs: '24px', md: '21px' },
              }}
            >
              {getInitials(currentOption.label)}
            </Typography>
          </Box>
          <Divider sx={{ borderColor: '#FFFFFF0D', paddingTop: '12px' }} />
        </>
      ) : (
        <Box style={{ position: 'relative' }} ref={dropdownRef}>
          <Box className={classes.dropdown} onClick={() => setIsOpen((prevState) => !prevState)}>
            <Typography
              sx={{
                color: COLORS.brand.white,
                textWrap: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                fontWeight: '400',
                fontSize: { xs: '16px', md: '14px' },
                lineHeight: { xs: '24px', md: '21px' },
              }}
              title={currentOption.label}
            >
              {currentOption.label}
            </Typography>
            <Box style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center' }}>
              {isOpen ? <ChevronUpIcon height={16} /> : <ChevronDownIcon height={16} />}
            </Box>
          </Box>
          {isOpen && (
            <Box className={classes.dropdownList}>
              <ul className={classes.list}>
                {options.map((item) => (
                  <li className={classes.option} key={item.value} onClick={() => handleSelectOption(item)}>
                    <Typography
                      className={classes.itemLabel}
                      sx={{
                        fontSize: { xs: '16px', md: '14px' },
                        lineHeight: { xs: '24px', md: '21px' },
                      }}
                      title={item.label}
                    >
                      {item.label}
                    </Typography>
                    {item.value === currentOption.value && (
                      <Box className={classes.optionIcon}>
                        <CheckIcon />
                      </Box>
                    )}
                  </li>
                ))}
              </ul>
              <Divider />
              <ul className={classes.list}>
                {actions.map((item) => (
                  <li
                    className={classes.listItem}
                    key={item.label}
                    onClick={() => (!item.disabled ? item.onClick() : null)}
                    style={{ cursor: item.disabled ? 'not-allowed' : 'pointer' }}
                  >
                    <Box
                      style={{
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        color: COLORS.brand.night,
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      className={classes.itemLabel}
                      sx={{
                        fontSize: { xs: '16px', md: '14px' },
                        lineHeight: { xs: '24px', md: '21px' },
                      }}
                    >
                      {item.label}
                    </Typography>
                  </li>
                ))}
              </ul>
            </Box>
          )}
          <Divider sx={{ borderColor: '#FFFFFF0D', paddingTop: '12px' }} />
        </Box>
      )}
    </>
  );
};

export default DropdownListWithActions;
